import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Flex } from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
} from "@ant-design/icons";

import SbTable  from "uiCore/SbTable/SbTable";
import SbInput  from "uiCore/SbInput/SbInput";
import SbButton from "uiCore/SbButton/SbButton";
import SbCard from "uiCore/SbCard/SbCard";
import SbDivider from "uiCore/SbDivider/SbDivider";
import { getJdTemplates, createJdTemplate, updateJdTemplate } from "Api/apiService";
import { formatTimestamp, toTimestampMs } from "Utils/timeFormat";
import CreateTemplateModal from "./CreateTemplateModal";

import { PageWrapperStyled } from "./JdTemplates.styles";
import AdminPageHeader from "Pages/AdminSetup/components/AdminPageHeader";

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────

const JdTemplates = () => {
  const navigate = useNavigate();

  const [search, setSearch]       = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getJdTemplates()
      .then((response) => {
        setTemplates(response?.templates ?? []);
      })
      .catch((err) => {
        console.error("Failed to fetch templates:", err);
        setTemplates([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleCreateSubmit = async (values) => {
    setModalOpen(false);
    setLoading(true);
    try {
      const data = await createJdTemplate({
        templateName: values.templateName ?? "",
        legalEntity:  values.legalEntity  ?? "",
        country:      values.country      ?? "",
      });

      // Always use the unique id returned by create — never "new" — so the
      // detail page edits this same record instead of creating a duplicate.
      const newId = data?.sb_template_unique_id;
      if (!newId) {
        console.error("Create template did not return a template id:", data);
        setLoading(false);
        return;
      }

      // When starting from an existing template, persist its markdown right away
      // (against the new id) so the referred content is saved even if the admin
      // cancels on the detail page.
      if (values.sourceMarkdown) {
        await updateJdTemplate(newId, {
          templateName: values.templateName ?? "",
          country:      values.country      ?? "",
          markdown:     values.sourceMarkdown,
        });
      }

      navigate(`/admin-setup/jd-templates/${newId}`, {
        state: {
          templateName: values.templateName ?? "",
          legalEntity:  values.legalEntity  ?? "",
          country:      values.country      ?? "",
          markdown:     values.sourceMarkdown ?? "",
        },
      });
    } catch (err) {
      console.error("Failed to create template:", err);
      setLoading(false);
    }
  };

  const COLUMNS = [
    { title: "S.NO",          key: "sno",                   width: 70, render: (_, __, index) => index + 1 },
    { title: "TEMPLATE NAME", dataIndex: "org_jd_template_name",  key: "org_jd_template_name",  width: 360, render: (v) => <strong>{v ?? "—"}</strong> },
    { title: "COUNTRY",       dataIndex: "sb_org_template_value", key: "sb_org_template_value", width: 200 },
    {
      title: "LAST UPDATED",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 220,
      defaultSortOrder: "descend",
      sorter: (a, b) => toTimestampMs(a.updatedAt) - toTimestampMs(b.updatedAt),
      render: (ts) => formatTimestamp(ts) || "—",
    },
    {
      title: "ACTION",
      key: "action",
      width: 100,
      render: (_, record) => (
        <SbButton
          buttonType="action"
          icon={<EditOutlined />}
          onClick={() => navigate(`/admin-setup/jd-templates/${record.sb_template_unique_id}`)}
        />
      ),
    },
   
  ];

  const filteredTemplates = templates?.filter((t) =>
    (t.org_jd_template_name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PageWrapperStyled>

      <AdminPageHeader
        title="Jobs Library — JD Templates"
        onBack={() => navigate(-1)}
        rightActions={
          <SbButton buttonType="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            New Template
          </SbButton>
        }
      />

      <SbDivider style={{ margin: "0 0 24px 0" }} />

      <SbCard>
        <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
          <div style={{ width: 280 }}>
            <SbInput
              placeholder="Search templates..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Text type="secondary">{filteredTemplates.length} templates</Text>
        </Flex>

        <SbTable
          dataSource={filteredTemplates?.map((t) => ({ ...t, key: t.sb_template_unique_id })) || []}
          columns={COLUMNS}
          pagination={false}
          rowKey="key"
          size="middle"
          loading={loading}
        />
      </SbCard>

     {modalOpen && (
        <CreateTemplateModal
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreateSubmit}
          templates={templates}
        />
      )}

    </PageWrapperStyled>
  );
};

export default JdTemplates;
